import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import type { ITimelineMessage } from '../models/timeline.models';

export interface SplitEmailResult {
  replyHtml: string;
  quotedHtml: string | null;
}

// ── Quote detection: patterns used by content-based fallback ─────────────────
// Email header labels (From/To/Date/Subject) in common languages. Used to detect
// quoted blocks without relying on client-specific IDs/classes.
const EMAIL_HEADER_FROM = /(?:^|\s)(?:From|Da|De|Von|Från)\s*:/im;
const EMAIL_HEADER_DATE = /(?:^|\s)(?:Date|Sent|Inviato|Envoyé|Gesendet|Datum)\s*:/im;
const EMAIL_HEADER_TO = /(?:^|\s)(?:To|A)\s*:/im;
const EMAIL_HEADER_SUBJECT = /(?:^|\s)(?:Subject|Oggetto|Objet|Betreff|Ämne)\s*:/im;
/** Gmail/Apple Mail style citation line before a blockquote. */
const CITATION_WROTE = /On\s+.+?\s+wrote\s*:/im;

/**
 * Splits an email body into the reply content and the quoted/forwarded original.
 *
 * Detection order:
 * 1. Fast-path: client-specific selectors (Outlook, Gmail, OWA).
 * 2. Fallback: content-based detection of email header blocks (works across
 *    clients and languages).
 * 3. Last resort: generic blockquote.
 */
export function splitEmailQuote(html: string): SplitEmailResult {
  if (!html) return { replyHtml: '', quotedHtml: null };

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // ── 1. Fast-path: known client markers ───────────────────────────────────
    const quoteAnchor =
      doc.querySelector('#divRplyFwdMsg') ??
      doc.querySelector('#appendonsend') ??
      doc.querySelector('.gmail_quote') ??
      findOutlookBorderSeparator(doc) ??
      doc.querySelector('#mail-editor-reference-message-container') ??
      findFirstEmailHeaderBlock(doc) ??
      doc.querySelector('blockquote');

    if (!quoteAnchor) {
      return { replyHtml: html, quotedHtml: null };
    }

    let splitNode: Element = quoteAnchor;

    // Include preceding <hr> when the anchor is Outlook’s reply/forward block
    if (quoteAnchor.id === 'divRplyFwdMsg') {
      const prev = quoteAnchor.previousElementSibling;
      if (prev?.tagName === 'HR') splitNode = prev;
    }

    // For #appendonsend, the visible quote starts at the <hr> that follows it
    if (quoteAnchor.id === 'appendonsend') {
      const next = quoteAnchor.nextElementSibling;
      if (next?.tagName === 'HR') splitNode = next;
    }

    // For blockquote anchors, include the preceding "On ... wrote:" line
    if (splitNode.tagName === 'BLOCKQUOTE') {
      const prev = splitNode.previousElementSibling;
      if (prev && CITATION_WROTE.test(prev.textContent || '')) splitNode = prev;
    }

    // When content-based detection picked a body child, include preceding <hr> or border-top separator
    if (splitNode.parentElement === doc.body) {
      const prev = splitNode.previousElementSibling;
      if (prev?.tagName === 'HR') splitNode = prev;
      else if (prev && isSeparatorDiv(prev)) splitNode = prev;
    }

    // ── Collect quoted nodes and build result ─────────────────────────────────
    const quotedNodes: Node[] = [];
    let current: Node | null = splitNode;
    while (current) {
      quotedNodes.push(current);
      current = current.nextSibling;
    }

    quotedNodes.forEach((n) => n.parentNode?.removeChild(n));
    const replyHtml = doc.body.innerHTML.trim();

    const quotedContainer = doc.createElement('div');
    quotedNodes.forEach((n) => quotedContainer.appendChild(n));
    const quotedHtml = quotedContainer.innerHTML.trim();

    if (!quotedHtml) return { replyHtml: html, quotedHtml: null };
    return { replyHtml, quotedHtml };
  } catch {
    return { replyHtml: html, quotedHtml: null };
  }
}

/**
 * True if the element is a div with border-top style (common quote separator).
 */
function isSeparatorDiv(el: Element): boolean {
  if (el.tagName !== 'DIV') return false;
  return /border-top\s*:\s*solid/i.test(el.getAttribute('style') || '');
}

/**
 * Finds the first block (in document order) that looks like the start of a
 * quoted message: email header lines (From/To/Date/Subject in various languages)
 * or "On ... wrote:" citation. Returns the body direct child that contains it,
 * so we can split there and treat it and following siblings as quoted.
 * Used as a universal fallback when no client-specific selector matches.
 */
function findFirstEmailHeaderBlock(doc: Document): Element | null {
  const body = doc.body;
  if (!body) return null;

  const all = body.getElementsByTagName('*');
  for (let i = 0; i < all.length; i++) {
    const el = all[i];
    if (!looksLikeEmailHeaderBlock(el.textContent || '')) continue;
    // Use the ancestor that is a direct child of body as the split point
    let node: Element | null = el;
    while (node?.parentElement && node.parentElement !== body) node = node.parentElement;
    return node;
  }
  return null;
}

/**
 * Returns true if the text contains a typical quoted-email header block:
 * at least two of From/Date/To/Subject (any language) or "On ... wrote:".
 */
function looksLikeEmailHeaderBlock(text: string): boolean {
  if (!text.trim()) return false;
  let count = 0;
  if (EMAIL_HEADER_FROM.test(text)) count++;
  if (EMAIL_HEADER_DATE.test(text)) count++;
  if (EMAIL_HEADER_TO.test(text)) count++;
  if (EMAIL_HEADER_SUBJECT.test(text)) count++;
  if (count >= 2) return true;
  return CITATION_WROTE.test(text);
}

/**
 * Outlook Mobile / OWA: finds a div with border-top that contains a sender
 * label (From/Da/De/Von etc.). Returns the parent wrapper so the whole
 * quoted block is captured.
 */
function findOutlookBorderSeparator(doc: Document): Element | null {
  const divs = doc.querySelectorAll('div');
  for (let i = 0; i < divs.length; i++) {
    const div = divs[i];
    if (!/border-top\s*:\s*solid/i.test(div.getAttribute('style') || '')) continue;
    const text = (div.textContent || '').trim();
    if (!EMAIL_HEADER_FROM.test(text)) continue;
    return div.parentElement && div.parentElement !== doc.body
      ? div.parentElement
      : div;
  }
  return null;
}

/** Replace any run of literal newlines (\\n, \\r) in HTML with a single <br>. */
function replaceNewlinesWithBr(html: string): string {
  if (!html) return html;
  return html.replace(/(?:\r\n|\r|\n)+/g, '<br>');
}

/**
 * Same cleaning pipeline as sanitizeHtml but returns a plain string (for parsing only, e.g. text preview).
 * Use this when you need cleaned HTML as string, not for binding with [innerHTML].
 */
export function getCleanedHtmlString(html: string): string {
  if (!html) return '';
  try {
    let cleanedHtml = stripMetadataTags(html);
    // cleanedHtml = replaceNewlinesWithBr(cleanedHtml);
    cleanedHtml = removeAllStyleTags(cleanedHtml);
    return cleanedHtml.trim();
  } catch (error) {
    console.error('Error in getCleanedHtmlString, returning original:', error);
    return html;
  }
}

/**
 * Sanitize HTML content and scope styles to message-body container
 */
export function sanitizeHtml(html: string, sanitizer: DomSanitizer): SafeHtml {
  if (!html) return sanitizer.bypassSecurityTrustHtml('');

  try {
    const cleanedHtml = getCleanedHtmlString(html);

    return sanitizer.bypassSecurityTrustHtml(cleanedHtml);
  } catch (error) {
    console.error('Error in sanitizeHtml, returning original:', error);
    // If processing fails, at least allow the HTML through
    return sanitizer.bypassSecurityTrustHtml(html);
  }
}

/**
 * Strip useless metadata tags from HTML
 */
export function stripMetadataTags(html: string): string {
  if (!html) return html;
  
  try {
    let cleaned = html;
    
    // Remove <head> tags and their content (but preserve <style> tags inside)
    cleaned = cleaned.replace(/<head[^>]*>([\s\S]*?)<\/head>/gi, (match, headContent) => {
      // Extract style tags from head to preserve them
      const styleTags = headContent.match(/<style[^>]*>[\s\S]*?<\/style>/gi) || [];
      return styleTags.join('');
    });
    
    // Remove individual metadata tags
    cleaned = cleaned.replace(/<meta[^>]*>/gi, '');
    cleaned = cleaned.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');
    cleaned = cleaned.replace(/<link[^>]*>/gi, '');
    cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    cleaned = cleaned.replace(/<base[^>]*>/gi, '');
    cleaned = cleaned.replace(/<noscript[^>]*>[\s\S]*?<\/noscript>/gi, '');
    
    // Remove HTML comments (optional, but they're metadata)
    cleaned = cleaned.replace(/<!--[\s\S]*?-->/g, '');
    
    // Remove <html> and <body> wrapper tags but keep their content
    cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/?body[^>]*>/gi, '');
    
    return cleaned.trim();
  } catch {
    return html;
  }
}

/**
 * Remove all style tags from HTML
 */
export function removeAllStyleTags(html: string): string {
  if (!html) return html;
  
  try {
    // Remove all <style> tags and their content
    return html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  } catch {
    return html;
  }
}

/**
 * Builds a CKEditor-friendly forwarded message body for display in the compose editor.
 * Uses simple HTML that CKEditor won't strip (no <font>, no id attributes, etc.).
 */
export function buildForwardEditorBody(message: ITimelineMessage): string {
  const date = formatEmailDate(message.timestamp);
  const from = message.sender_name
    ? `${escapeHtml(message.sender_name)} &lt;${escapeHtml(message.sender_email)}&gt;`
    : escapeHtml(message.sender_email);
  const to = escapeHtml(message.recipients || '');
  const subject = escapeHtml(message.subject || '');

  const headerLines = [
    `<strong>From:</strong> ${from}`,
    `<strong>Date:</strong> ${date}`,
    `<strong>To:</strong> ${to}`,
    message.cc ? `<strong>Cc:</strong> ${escapeHtml(message.cc)}` : '',
    `<strong>Subject:</strong> ${subject}`,
  ].filter(Boolean).join('<br>');

  const cleanBody = getCleanedHtmlString(message.body || '');

  return [
    '<p>&nbsp;</p>',
    '<div class="gmail_quote">',
      '<p>---------- Forwarded message ----------</p>',
      `<p>${headerLines}</p>`,
      '<p>&nbsp;</p>',
      cleanBody,
    '</div>',
  ].join('');
}

/**
 * Builds a Gmail-style quoted reply body for the compose editor.
 * Used to inject the original message as a blockquote when the user
 * clicks the "..." toggle in the reply compose panel.
 */
export function buildReplyEditorBody(message: ITimelineMessage): string {
  const date = formatEmailDate(message.timestamp);
  const sender = message.sender_name
    ? `${escapeHtml(message.sender_name)} &lt;${escapeHtml(message.sender_email)}&gt;`
    : escapeHtml(message.sender_email);

  const cleanBody = getCleanedHtmlString(message.body || '');

  return [
    '<p>&nbsp;</p><p>&nbsp;</p>',
    '<div class="gmail_quote">',
      `<div class="gmail_attr">On ${date}, ${sender} wrote:</div>`,
      `<blockquote class="gmail_quote" style="margin:0px 0px 0px 0.8ex;border-left:1px solid rgb(204,204,204);padding-left:1ex">`,
        cleanBody,
      '</blockquote>',
    '</div>',
  ].join('');
}

function formatEmailDate(timestamp: string): string {
  try {
    const d = new Date(timestamp);
    return d.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return timestamp;
  }
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return text.replace(/[&<>"']/g, (ch) => map[ch]);
}
